import { useEffect, useRef, useState } from "react";
import { useBranding, resolveLogoUrl } from "@/context/BrandingContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, Palette, Upload, Mail } from "lucide-react";

export default function PersonalizacaoTab() {
  const { branding, update, reload } = useBranding();
  const [form, setForm] = useState({ ...branding, owner_email: "", notify_on_new_order: true });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadAdmin = async () => {
    try {
      const { data } = await api.get("/settings/admin");
      setForm({ ...data });
    } catch { setForm({ ...branding }); }
  };
  useEffect(() => { loadAdmin(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await update({
        company_name: form.company_name,
        welcome_text: form.welcome_text,
        primary_color: form.primary_color,
        accent_color: form.accent_color,
        owner_email: form.owner_email,
        notify_on_new_order: form.notify_on_new_order,
      });
      toast.success("Personalização salva");
    } catch { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Logo deve ter no máximo 4MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(prev => ({ ...prev, logo_url: data.url }));
      await reload();
      toast.success("Logo enviado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    try {
      await update({ logo_url: "" });
      setForm(prev => ({ ...prev, logo_url: "" }));
      toast.success("Logo removido");
    } catch { toast.error("Erro ao remover"); }
  };

  const previewLogo = resolveLogoUrl(form.logo_url);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2"><Palette className="h-5 w-5" /> Personalização</CardTitle>
        <CardDescription>Ajuste cores, logo, textos e notificações do portal.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label>Nome da empresa</Label>
            <Input data-testid="brand-name" value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Cor primária</Label>
            <div className="flex gap-3 items-center">
              <input type="color" value={form.primary_color || "#E05D36"} onChange={(e) => set("primary_color", e.target.value)}
                     data-testid="brand-primary-color" className="h-11 w-14 rounded cursor-pointer border border-slate-200" />
              <Input value={form.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} className="font-mono" />
            </div>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Texto de boas-vindas</Label>
            <Textarea data-testid="brand-welcome" rows={2} value={form.welcome_text || ""} onChange={(e) => set("welcome_text", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Cor de destaque</Label>
            <div className="flex gap-3 items-center">
              <input type="color" value={form.accent_color || "#0F172A"} onChange={(e) => set("accent_color", e.target.value)}
                     data-testid="brand-accent-color" className="h-11 w-14 rounded cursor-pointer border border-slate-200" />
              <Input value={form.accent_color || ""} onChange={(e) => set("accent_color", e.target.value)} className="font-mono" />
            </div>
          </div>
        </div>

        {/* Logo upload */}
        <div className="border border-slate-200 rounded-lg p-6 bg-white">
          <div className="flex items-start gap-6 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Logo atual</div>
              {previewLogo ? (
                <img src={previewLogo} alt="logo" className="h-24 w-24 rounded-lg object-cover border border-slate-200" />
              ) : (
                <div className="h-24 w-24 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-xs">Sem logo</div>
              )}
            </div>
            <div className="flex-1 min-w-[240px] space-y-3">
              <div>
                <Label className="mb-2 block">Enviar novo logo</Label>
                <p className="text-sm text-slate-500 mb-3">PNG, JPG, WEBP ou SVG. Máximo 4MB.</p>
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} data-testid="logo-file-input" className="hidden" />
              <div className="flex gap-2">
                <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="logo-upload-btn" className="btn-brand rounded-full">
                  <Upload className="h-4 w-4 mr-2" /> {uploading ? "Enviando..." : "Escolher arquivo"}
                </Button>
                {form.logo_url && (
                  <Button type="button" variant="outline" onClick={removeLogo} data-testid="logo-remove-btn" className="rounded-full">Remover</Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Notification config */}
        <div className="border border-slate-200 rounded-lg p-6 bg-white space-y-4">
          <div className="flex items-center gap-2 text-slate-900 font-heading font-bold">
            <Mail className="h-5 w-5" /> Notificação por e-mail
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-slate-900">Receber e-mail a cada novo pedido</Label>
              <p className="text-sm text-slate-500">Enviamos um resumo do pedido para o endereço abaixo.</p>
            </div>
            <Switch
              checked={!!form.notify_on_new_order}
              onCheckedChange={(v) => set("notify_on_new_order", v)}
              data-testid="brand-notify-switch"
            />
          </div>
          <div className="space-y-2">
            <Label>E-mail que receberá a notificação</Label>
            <Input
              data-testid="brand-owner-email"
              type="email"
              value={form.owner_email || ""}
              onChange={(e) => set("owner_email", e.target.value)}
              placeholder="seu@email.com"
            />
          </div>
        </div>

        {/* Preview */}
        <div className="border border-slate-200 rounded-lg p-6 bg-slate-50">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">Prévia</div>
          <div className="flex items-center gap-3 mb-3">
            {previewLogo ? (
              <img src={previewLogo} alt="preview" className="h-10 w-10 rounded object-cover" />
            ) : (
              <div className="h-10 w-10 rounded" style={{ backgroundColor: form.primary_color }} />
            )}
            <div className="font-heading font-bold">{form.company_name}</div>
          </div>
          <div className="text-slate-700 mb-4">{form.welcome_text}</div>
          <button className="rounded-full px-5 py-2 text-white text-sm" style={{ backgroundColor: form.primary_color }}>
            Botão de exemplo
          </button>
        </div>

        <Button onClick={save} disabled={saving} data-testid="brand-save" className="btn-brand rounded-full h-11 px-8">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Salvando..." : "Salvar personalização"}
        </Button>
      </CardContent>
    </Card>
  );
}
